import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { Lock } from '@mui/icons-material'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

/** Правила зеркалят серверные (ChangePasswordRequest): 10+ символов, оба регистра и цифра. */
const createChangePasswordSchema = (t: (key: string) => string) =>
  z
    .object({
      current_password: z.string().min(1, t('profile.security.validation.currentRequired')),
      new_password: z
        .string()
        .min(10, t('profile.security.validation.newMin'))
        .regex(/[a-z]/, t('profile.security.validation.newComplexity'))
        .regex(/[A-Z]/, t('profile.security.validation.newComplexity'))
        .regex(/[0-9]/, t('profile.security.validation.newComplexity')),
      new_password_confirmation: z.string().min(1, t('profile.security.validation.confirmRequired')),
      logout_other_sessions: z.boolean(),
    })
    .refine((data) => data.new_password === data.new_password_confirmation, {
      message: t('profile.security.validation.mismatch'),
      path: ['new_password_confirmation'],
    })
    .refine((data) => data.new_password !== data.current_password, {
      message: t('profile.security.validation.sameAsCurrent'),
      path: ['new_password'],
    })

export function ProfileSecurityPage() {
  const { t } = useTranslation()
  const updateToken = useAuthStore((state) => state.updateToken)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const changePasswordSchema = createChangePasswordSchema(t)
  type ChangePasswordFormData = z.infer<typeof changePasswordSchema>

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
      logout_other_sessions: false,
    },
  })

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      setError(null)
      setSuccess(null)
      setIsSubmitting(true)

      const freshToken = await authApi.changePassword(data)

      // Прочие сессии завершены — текущий токен тоже инвалидирован, меняем его на свежий.
      if (freshToken) {
        updateToken(freshToken.token, freshToken.expires_in)
      }

      setSuccess(
        data.logout_other_sessions
          ? t('profile.security.successWithLogout')
          : t('profile.security.success'),
      )
      reset()
    } catch (err: unknown) {
      setError(displayErrorMessage(getErrorMessage(err), t('profile.security.error')))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('profile.security.title')}
          </Typography>
          <PageHelpPopover pageKey="profile-security" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('profile.security.changePassword')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('profile.security.changePasswordHint')}
            </Typography>

            {/* Карточка во всю ширину, как на остальных страницах профиля;
                ограничена только колонка полей — строка ввода пароля во всю
                ширину экрана читается плохо. */}
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}
            >
              <Controller
                name="current_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('profile.security.currentPassword')}
                    type="password"
                    fullWidth
                    autoComplete="current-password"
                    error={!!errors.current_password}
                    helperText={errors.current_password?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="new_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('profile.security.newPassword')}
                    type="password"
                    fullWidth
                    autoComplete="new-password"
                    error={!!errors.new_password}
                    helperText={
                      errors.new_password?.message ?? t('profile.security.newPasswordHint')
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="new_password_confirmation"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('profile.security.confirmPassword')}
                    type="password"
                    fullWidth
                    autoComplete="new-password"
                    error={!!errors.new_password_confirmation}
                    helperText={errors.new_password_confirmation?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="logout_other_sessions"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t('profile.security.logoutOtherSessions')}
                  />
                )}
              />
              <Typography variant="body2" color="text.secondary">
                {t('profile.security.logoutOtherSessionsHint')}
              </Typography>

              <Box>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? t('common.loading') : t('profile.security.submit')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ProfileLayout>
  )
}
